import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import ProductDiscount from '@/components/product/ProductDiscount';
import ProductPricing from '@/components/product/ProductPricing';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import UploadField from '@/components/UI/UploadField';
import { IBrandsResponse } from '@/interfaces/brandsResponse.interface';
import { ICategoryData } from '@/interfaces/categoryResponse.interface';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import { IProduct } from '@/interfaces/product.interface';
import { IproductResponse } from '@/interfaces/productresponse.interface';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorMessage from '../../components/common/ErrorMessage';
import Breadcrumb from '@/components/common/Breadcrumb';
import ProductAddOns from '@/components/product/ProductAddOns';
import { errorTypes } from '@/utility/validation';
import {
  GetFilterUrl,
  showToastNotification,
  ThrowDateTimeError,
} from '@/utility/helper';
import { IVariantData, IVariantList } from '@/interfaces/VariantList.interface';
import BtnLoader from '@/components/UI/loaders/BtnLoader';

interface StatesProp {
  isLoading: boolean;
  categoryDetail: ICategoryData;
  brandDetail: IBrandsResponse;
  media: IMediaProp[];
  variants: IVariantData[];
}

const EditProduct: React.FC = () => {
  const metaPrefix = `productInfo.0.meta.`;
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = id;
  const [states, setStates] = useState<StatesProp>({
    isLoading: false,
    media: [],
    categoryDetail: {
      id: '',
      name: '',
      description: '',
      status: '',
      createdAt: '',
      updatedAt: '',
      deletedAt: '',
      media: [],
    },
    brandDetail: {
      id: '',
      name: '',
      description: '',
      status: '',
      createdAt: '',
      updatedAt: '',
      deletedAt: '',
      media: [],
    },
    variants: [],
  });

  const methods = useForm<IProduct>({
    defaultValues: {
      inventoryItemId: '',
      name: '',
      description: '',
      categoryId: '',
      brandId: '',
      itemCode: '',
      status: '',
      productInfo: [
        {
          loyaltyPoints: 0,
          price: '',
          quantity: 0,
          pieceWeight: '',
          isVatIncluded: false,
          discountPercentage: 0,
          meta: {
            refund: {
              isRefundable: false,
              window: 0,
            },
            employee: {
              discount: {
                type: 'flat',
                value: 0,
              },
            },
            addons: {
              product: {
                ids: [],
              },
            },
          },
          discountedPrice: '',
          discountStartDate: '',
          discountEndDate: '',
          productInfoId: '',
          discountLabel: '',
        },
      ],
      media: [],
      variantId: '',
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const isRefundable = watch(`${metaPrefix}refund.isRefundable`);

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchVariants = async () => {
    try {
      const filterQuery = {
        page: 1,
        limit: 20,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}/${API_ENDPOINT.variants}?${query ? query : ''}`;
      const res = await callApi<IVariantList>(PATH, { method: 'GET' });
      handleChangeState({
        variants: res.data,
      });
    } catch (error) {
      ThrowError(error);
    }
  };

  const fetchProductInfo = async () => {
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${productId}`;
      const res = await callApi<IproductResponse>(PATH, { method: 'GET' });
      const updatedProductInfo = res.productInfo.map((info) => ({
        ...info,
        productInfoId: info.id || '',
        meta: {
          refund: {
            isRefundable: info.meta?.refund?.isRefundable ?? false,
            window: info.meta?.refund?.window ?? 0,
          },
          employee: {
            discount: {
              type: info.meta?.employee?.discount?.type ?? 'flat',
              value: info.meta?.employee?.discount?.value ?? 0,
            },
          },
          addons: {
            product: {
              ids: info.meta?.addons?.product?.ids ?? [],
            },
          },
        },
      }));
      fetchCategoryBrandInfo(res.categoryId, res.brandId);
      handleChangeState({
        media: res.directImageUrl
          ? [{ type: 'image', altText: '', url: res.directImageUrl }]
          : res.media,
      });
      reset({ ...res, productInfo: updatedProductInfo });
    } catch (error) {
      console.log(error);
    }
  };

  const selectedAddOnIds = watch('productInfo.0.meta.addons.product.ids') || [];
  const fetchCategoryBrandInfo = async (
    categoryId: string,
    brandId: string,
  ) => {
    try {
      const CATEGORY_PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${categoryId}`;
      const BRAND_PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${brandId}`;
      const [categoryRes, brandRes] = await Promise.all([
        callApi(CATEGORY_PATH, { method: 'GET' }),
        callApi(BRAND_PATH, { method: 'GET' }),
      ]);
      handleChangeState({ categoryDetail: categoryRes, brandDetail: brandRes });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchVariants();
  }, []);

  useEffect(() => {
    productId && fetchProductInfo();
  }, [productId]);

  const onSubmit = async (data: IProduct) => {
    const info = data.productInfo?.[0];
    if (info?.discountStartDate && info?.discountEndDate) {
      const start = new Date(info.discountStartDate);
      const end = new Date(info.discountEndDate);
      const message = ThrowDateTimeError(start, end);
      if (message) showToastNotification('danger', message);
    }

    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${productId}`;
      const productInfo = data.productInfo[0];
      const payload = {
        ...data,
        discount:
          (productInfo.discountLabel || productInfo.discountStartDate) === null
            ? null
            : {
                label: productInfo.discountLabel,
                id: [productId],
                discountPercentage: productInfo.discountPercentage,
                discountStartDate: productInfo.discountStartDate,
                discountEndDate: productInfo.discountEndDate,
                type: productInfo.discountPercentage === 0 ? 'remove' : 'add',
              },
      };
      await callApi(PATH, { method: 'PATCH', data: payload });
      showToastNotification('success', 'Product detail updated successfully');
      handleChangeState({ isLoading: false });
      navigate(RoutesLink.products);
    } catch (error) {
      handleChangeState({ isLoading: false });
      ThrowError(error);
    }
  };

  const breadcrumb = [
    { label: 'Products', href: RoutesLink.products },
    { label: 'Edit Product' },
  ];

  return (
    <>
      <Layout>
        <Breadcrumb items={breadcrumb} />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Card>
              <div className="space-y-5">
                {/* About Product */}
                <h2 className="text-lg font-semibold mb-4">About Product</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FormField
                      label="Name"
                      type="text"
                      placeholder="Enter product name"
                      {...register('name')}
                    />
                  </div>
                  <FormField
                    label="Inventory Id"
                    type="text"
                    {...register('inventoryItemId')}
                    readOnly
                    disabled
                  />
                  <FormField
                    label="Item Code"
                    type="text"
                    {...register('itemCode')}
                    readOnly
                    disabled
                  />
                  <div>
                    <FormField
                      label="Item Type"
                      type="select"
                      options={states.variants.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      {...register('variantId')}
                    />
                  </div>

                  <FormField
                    label="Category"
                    type="text"
                    value={states.categoryDetail.name}
                    readOnly
                    disabled
                  />

                  <FormField
                    label="Brand"
                    type="text"
                    value={states.brandDetail.name}
                    readOnly
                    disabled
                  />
                </div>

                <div className="">
                  <FormField
                    type="textarea"
                    label="Description"
                    placeholder="Start describing the product..."
                    {...register('description', {
                      ...errorTypes.required('description'),
                    })}
                    rows={4}
                  />
                  <ErrorMessage message={errors.description?.message} />
                </div>

                {/* Upload */}
                <div>
                  <h4 className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Product Images
                  </h4>
                  <UploadField
                    referenceId={productId as string}
                    referenceType="product"
                    initialMedia={states.media}
                    multiple={false}
                  />
                </div>
              </div>
            </Card>

            {/* Pricing */}

            {/* Public Pricing */}
            <ProductPricing type="public" register={register} watch={watch} />
            {/* <ProductPricing
              type="employee"
              register={register}
              watch={watch}
            /> */}
            <ProductAddOns
              productId={productId as string}
              selectedAddOnIds={selectedAddOnIds}
            />
            {/* Employee Pricing */}
            {/* <ProductPricing type='employee' register={register} /> */}

            {/* PRODUCT VARIANTS */}
            {/* <AddVariant /> */}
            {/* Product Organization */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> */}
            {/* <Card>
              <h3 className="text-lg font-semibold mb-6">
                Product Organization
              </h3>
              <div className="space-y-5">
                <FormField
                  label="Collection"
                  type="select"
                  options={[
                    'Select Collection',
                    'Collection 1',
                    'Collection 2',
                  ]}
                />

                <FormField label="Tags" placeholder="Enter tags" />

                <FormField
                  label="Rating"
                  type="number"
                  placeholder="Enter rating"
                />
              </div>
            </Card> */}

            {/* Shipping */}
            {/* <Card>
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-6">Shipping</h3>
                <FormField
                  type="checkbox"
                  label="Add Product Weight"
                  defaultChecked
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Weight"
                    type="number"
                    placeholder="Enter weight"
                  />
                  <FormField
                    label="Unit"
                    type="select"
                    options={['gm', 'kg']}
                  />
                </div>
              </div>
            </Card> */}
            {/* </div> */}

            <ProductDiscount
              register={register}
              watch={watch}
              setValue={setValue}
              error={errors}
              control={control}
            />
            <Card>
              <h2 className="text-lg font-semibold mb-4">Refund Policy</h2>
              <div>
                <FormField
                  label="Refundable"
                  type="checkbox"
                  {...register(`${metaPrefix}refund.isRefundable`)}
                />
                {isRefundable && (
                  <div className="mt-4">
                    <FormField
                      type="number"
                      label="Max Days"
                      min={1}
                      max={2}
                      placeholder="Enter Max Refundable Days"
                      {...register(`${metaPrefix}refund.window`, {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                )}
              </div>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button disabled={states.isLoading} className="btn-primary gap-2">
                {states.isLoading && <BtnLoader />} Save
              </button>
            </div>
          </form>
        </FormProvider>
      </Layout>
    </>
  );
};

export default EditProduct;
